import React, { useMemo, useEffect, useCallback, useState } from "react";
import {
  Modal,
  Popover,
  Button,
  Form,
  Input,
  Row,
  Col,
  Select,
  DatePicker,
} from "antd";
import dayjs from "dayjs"

import { patientsService } from "../../../services/patients";
import { openSuccessNotification } from "../../../utils/notifications";
import { getSelectOptionsWithOrWithoutImg } from "../../../utils/options";

import styles from "../../styles/Registers.module.css";
import { showApiErrors } from "../../../utils/showApiErrors";

import { useContext } from "react";
import { OrdinationsAndCountriesContext } from "../../../contexts/ordinationsAndCountriesContext";

const { TextArea } = Input;
const dateFormat = "DD/MM/YYYY";

export const AddOrEditPatient = ({
  visibleModal,
  onCloseModal,
  onAddPatient,
  onEdit,
  patient,
}) => {
  const { countries } = useContext(OrdinationsAndCountriesContext);
  const [patients, setPatients] = useState([]);
  const [loading, setLoading] = useState(false);
  const [form] = Form.useForm();

  const initialValues = useMemo(() => {
    if (!patient) {
      return undefined;
    }
    const {
      first_name,
      last_name,
      ucn,
      csn,
      psn,
      birth_date,
      phone_number,
      email,
      city,
      address,
      country,
    } = patient;
    return {
      first_name,
      last_name,
      ucn,
      csn,
      psn,
      birth_date: birth_date
        ? dayjs(String(birth_date).split("-").reverse().join("/"), dateFormat)
        : null,
      phone_number,
      email,
      city,
      address,
      country: country.id,
    };
  }, [patient]);

  useEffect(() => {
    async function getPatients() {
      setLoading(true);
      try {
        const { results } = await patientsService.getAllPatients();
        setPatients(results);
      } catch (e) {
        showApiErrors(e);
        console.log("ERROR 1");
      } finally {
        setLoading(false);
      }
    }
    getPatients();
  }, []);

  const resetFields = useCallback(() => {
    form.resetFields();
  }, [form]);

  useEffect(() => {
    if (visibleModal) {
      resetFields();
    }
  }, [resetFields, visibleModal]);

  const onUcnChange = (e) => {
    const ucn = e.target.value;

    const day = ucn.substring(0, 2);
    const month = ucn.substring(2, 4);
    const year =
      Number(ucn.substring(4, 7)) <= Number("099")
        ? "2".concat(ucn.substring(4, 7))
        : "1".concat(ucn.substring(4, 7));
    if (
      ucn.length == 7 &&
      Number(day) > 0 &&
      Number(day) <= 31 &&
      Number(month) > 0 &&
      Number(month) <= 12 &&
      Number(year)
    ) {
      const birthDate = [day, month, year].join("/");
      form.setFieldsValue({
        birth_date: dayjs(birthDate, dateFormat),
      });
    }
  };
  const transformData = async (fromForm) => {
    const {
      code,
      first_name,
      last_name,
      csn,
      ucn,
      psn,
      phone_number,
      email,
      city,
      country,
      address,
      birth_date,
    } = fromForm;
    let dataForPost = {
      code,
      first_name,
      last_name,
      csn,
      ucn,
      psn,
      phone_number,
      email,
      city,
      country,
      address,
      birth_date: birth_date ? birth_date.format("YYYY-MM-DD") : null,
    };
    return dataForPost;
  };

  const onSubmit = async (values) => {
    setLoading(true);
    const data = await transformData(values);
    if (patient) {
      try {
        await patientsService.editPatient(patient.id, data);
        onCloseModal();
        openSuccessNotification({ message: "Izmjena uspješno sačuvana!" });
        await onEdit();
      } catch (e) {
        showApiErrors(e);
        console.log("ERROR 2");
      } finally {
        setLoading(false);
      }
    } else {
      try {
        await patientsService.addPatient(data);
        openSuccessNotification({
          message: `Pacijent uspješno unešen!`,
        });
        console.log('tu');
        onCloseModal();
        await onAddPatient();
      } catch (e) {
        showApiErrors(e);
        console.log("ERROR 3");
      } finally {
        setLoading(false);
      }
    }
  };

  const type = patient ? "Uredi" : "Dodaj";

  function disabledDate(current) {
    // Can not select days before today and today
    return current && current > dayjs().endOf("day");
  }

  return (
    <Modal
      title={type + " pacijenta"}
      visible={visibleModal}
      wrapClassName={styles.addOrEditRegisterModal}
      onCancel={() => {
        onCloseModal();
      }}
      footer={[
        <Popover
          content={
            "Odustani od " +
            (patient ? "uređivanja" : "dodavanja") +
            " pacijenta"
          }
          trigger="hover"
        >
          <Button
            key="cancel"
            className={styles.cancelButton}
            onClick={() => {
              onCloseModal();
            }}
          >
            Odustani
          </Button>
        </Popover>,

        <Popover
          content={(patient ? "Uredi" : "Sačuvaj") + " pacijenta"}
          trigger="hover"
        >
          <Button
            key="submit"
            type="primary"
            loading={loading}
            onClick={() => form.submit()}
            className={styles.addButton}
          >
            Sačuvaj
          </Button>
        </Popover>,
      ]}
      width={600}
    >
      <div className={styles.modalFormWrapper}>
        <Form
          form={form}
          initialValues={initialValues}
          onFinish={onSubmit}
          onFinishFailed={({ errorFields }) => {
            form.scrollToField(errorFields[0].name);
          }}
        >
          <div className={styles.nameAndCheckboxFieldsWrapper}>
            <Row gutter={[16, 2]}>
              <Col xs={24} sm={12}>
                <Form.Item
                  label="Ime"
                  labelCol={{ span: 24 }}
                  className={styles.labelForAddOrEditPatient}
                  normalize={(value) =>
                    (value || "").replace(/[^a-zA-Za-åa-ö-w-я ]+/g, "")
                  }
                  name="first_name"
                  rules={[
                    {
                      max: 30,
                      message: "Ime mora sadržiti 30 ili manje karaktera ",
                    },
                    {
                      required: true,
                      message: "Popunite polje!",
                    },
                  ]}
                >
                  <Input
                    maxLength={30}
                    placeholder="Ime pacijenta"
                    autoComplete="off"
                  />
                </Form.Item>
              </Col>
              <Col xs={24} sm={12}>
                <Form.Item
                  label="Prezime"
                  normalize={(value) =>
                    (value || "").replace(/[^a-zA-Za-åa-ö-w-я ]+/g, "")
                  }
                  labelCol={{ span: 24 }}
                  className={styles.labelForAddOrEditPatient}
                  name="last_name"
                  rules={[
                    {
                      max: 30,
                      message: "Prezime mora sadržiti 30 ili manje karaktera ",
                    },
                    {
                      required: true,
                      message: "Popunite polje!",
                    },
                  ]}
                >
                  <Input
                    maxLength={30}
                    placeholder="Prezime pacijenta"
                    autoComplete="off"
                  />
                </Form.Item>
              </Col>
              <Col xs={24} sm={12}>
                <Form.Item
                  label="JMBG"
                  labelCol={{ span: 24 }}
                  className={styles.labelForAddOrEditPatient}
                  name="ucn"
                  //normalize={(value) => (value || "").replace(/[^0-9]+/g, "")}
                >
                  <Input
                    maxLength={20}
                    placeholder="JMBG pacijenta"
                    autoComplete="off"
                    onChange={onUcnChange}
                  />
                </Form.Item>
              </Col>
              <Col xs={24} sm={12}>
                <Form.Item
                  label="Datum rođenja"
                  labelCol={{ span: 24 }}
                  className={styles.labelForAddOrEditPatient}
                  name="birth_date"
                >
                  <DatePicker
                    //size="large"
                    className={styles.birthDatePicker}
                    format={dateFormat}
                    disabledDate={disabledDate}
                    placeholder="Datum rođenja"
                  />
                </Form.Item>
              </Col>
              <Col xs={24} sm={12}>
                <Form.Item
                  label="Broj lične karte"
                  labelCol={{ span: 24 }}
                  className={styles.labelForAddOrEditPatient}
                  name="csn"
                  //normalize={(value) => (value || "").replace(/[^0-9^]+/g, "")}
                >
                  <Input
                    maxLength={20}
                    placeholder="Broj lične karte pacijenta"
                    autoComplete="off"
                  />
                </Form.Item>
              </Col>
              <Col xs={24} sm={12}>
                <Form.Item
                  label="Broj pasoša"
                  labelCol={{ span: 24 }}
                  className={styles.labelForAddOrEditPatient}
                  name="psn"
                  //normalize={(value) => (value || "").replace(/[^0-9^]+/g, "")}
                >
                  <Input
                    placeholder="Broj pasoša pacijenta"
                    autoComplete="off"
                    maxLength={9}
                  />
                </Form.Item>
              </Col>

              <Col xs={24} sm={12}>
                <Form.Item
                  label="Kontakt telefon"
                  labelCol={{ span: 24 }}
                  //normalize={(value) => (value || "").replace(/[^0-9+]+/g, "")}
                  className={styles.labelForAddOrEditPatient}
                  name="phone_number"
                  rules={[
                    {
                      max: 30,
                      message:
                        "Kontakt telefon mora sadržiti 30 ili manje karaktera ",
                    },
                  ]}
                >
                  <Input
                    maxLength={30}
                    placeholder="Broj telefona"
                    autoComplete="off"
                  />
                </Form.Item>
              </Col>

              <Col xs={24} sm={12}>
                <Form.Item
                  label="Kontakt e-mail"
                  labelCol={{ span: 24 }}
                  rules={[
                    {
                      max: 40,
                      message: "E-mail mora sadržiti 40 ili manje karaktera ",
                    },

                    {
                      type: "email",
                      message: "Unešeni e-mail nije ispravan",
                    },
                  ]}
                  className={styles.labelForAddOrEditPatient}
                  name="email"
                >
                  <Input
                    maxLength={40}
                    placeholder="e-mail adresa"
                    autoComplete="off"
                  />
                </Form.Item>
              </Col>

              <Col xs={24} sm={12}>
                <Form.Item
                  label="Država"
                  labelCol={{ span: 24 }}
                  className={styles.labelForAddOrEditPatient}
                  name="country"
                  initialValue={141}
                >
                  <Select
                    showSearch
                    dropdownClassName={
                      countries
                        ? styles.selectDropdownWithImg
                        : styles.hideSelectDropdown
                    }
                    optionFilterProp="data-searchvalue"
                    showArrow={false}
                    placeholder="Izaberite državu"
                    notFoundContent={"Nema rezultata"}
                  >
                    {getSelectOptionsWithOrWithoutImg(countries || [])}
                  </Select>
                </Form.Item>
              </Col>

              <Col xs={24} sm={12}>
                <Form.Item
                  label="Grad"
                  labelCol={{ span: 24 }}
                  className={styles.labelForAddOrEditPatient}
                  name="city"
                  normalize={(value) =>
                    (value || "").replace(/[^a-zA-Za-åa-ö-w-я ]+/g, "")
                  }
                  rules={[
                    {
                      max: 20,
                      message: "Grad mora sadržiti 20 ili manje karaktera ",
                    },
                  ]}
                >
                  <Input maxLength={20} placeholder="Grad" autoComplete="off" />
                </Form.Item>
              </Col>

              <Col xs={24} sm={12}>
                <Form.Item
                  label="Adresa"
                  labelCol={{ span: 24 }}
                  maxLength={40}
                  className={styles.labelForAddOrEditPatient}
                  name="address"
                  rules={[
                    {
                      max: 40,
                      message: "Adresa mora sadržiti 40 ili manje karaktera ",
                    },
                  ]}
                >
                  <Input
                    maxLength={40}
                    placeholder="Adresa pacijenta"
                    autoComplete="off"
                  />
                </Form.Item>
              </Col>
            </Row>
          </div>
        </Form>
      </div>
    </Modal>
  );
};
